/**
 * AgroLogic.js - The "Sentry" AI Engine
 * 
 * Purpose: Deterministic Rules Engine to detect anomalies and generate 
 * agronomic alerts based on Sensor Data + Weather Data.
 * 
 * Logic Tier: Level 1 (Deterministic AI)
 */

export const agroLogic = {
    /**
     * Analyze current conditions and return a list of active alerts
     * @param {Object} sensorData - Current readings from SensorService
     * @param {Object} weatherData - Current weather from WeatherService
     * @param {string} cropType - 'Corn', 'Soybeans', 'Wheat'
     */
    analyze(sensorData, weatherData, cropType) {
        const alerts = [];

        // 1. DROUGHT RISK MONITOR
        // Logic: High Temp (>30C) + Low Moisture (<40%) + No Rain Forecast
        if (sensorData.temperature > 30 && sensorData.moisture < 45) {
            alerts.push({
                type: 'CRITICAL',
                title: 'Heat Stress Detected',
                message: `Soil moisture combined with high ambient temp (${sensorData.temperature}°C) indicates critical stress for ${cropType}.`,
                action: 'Activate irrigation immediately'
            });
        }

        // 2. NUTRIENT DEFICIENCY (Nitrogen Watch)
        // Corn is a heavy feeder. N < 40 is bad.
        if (cropType === 'Corn' && sensorData.nitrogen < 40) {
            alerts.push({
                type: 'WARNING',
                title: 'Nitrogen Deficiency',
                message: `N-levels (${sensorData.nitrogen} mg/kg) are suboptimal for Corn filling stage. Yield loss risk: High.`,
                action: 'Schedule side-dressing application'
            });
        }

        // 3. DISEASE RISK (Fungal)
        // Logic: High Humidity (>85%) + Moderate Temp (20-25C) = Fungal Bloom
        // Note: We use weather humidity if available, otherwise implied from soil moisture
        if (sensorData.moisture > 75 && (sensorData.temperature > 20 && sensorData.temperature < 28)) {
            alerts.push({
                type: 'WARNING',
                title: 'Fungal Risk: Pythium',
                message: 'Conditions match high-risk profile for Root Rot / Blight.',
                action: 'Deploy scout drone for visual inspection'
            });
        }

        // 4. LEAK DETECTION (The Sentry)
        // Logic: Moisture > 90% (Near saturation) but no recent rain known (Assumes we'd check weather history in full version)
        if (sensorData.moisture > 92) {
            alerts.push({
                type: 'ALERT',
                title: 'Saturation Alert / Possible Leak',
                message: 'Soil moisture is near saturation point (92%+). Check valves if no massive rainfall occurred.',
                action: 'Inspect Valve Sector 4'
            });
        }

        return alerts;
    }
};
